import React from 'react';
import {
  RadioButton,
  NumericTextBox,
  RadioButtonChangeEvent
} from '@progress/kendo-react-inputs';

import HelpSupport from '../help-support';
import { InGame } from '../../constants/settings';
import { InGameSettings } from '../../types/game-document/settings';

interface InGameSettingsProps {
  state: InGameSettings;
  onChange: (name: string, value: boolean) => void;
}

const InGameSetting = (props: InGameSettingsProps) => {
  const { state, onChange } = props;
  const [, setIsEnableLeaderboard] = React.useState<boolean>(false);

  const handleInGameSettingChange = (e: RadioButtonChangeEvent) => {
    const {
      target: { element },
      value
    } = e;
    if (!element) return;

    const { name } = element;
    onChange(name, value);

    // set task proximity value cant be 0 or below 0
    // cant be null too
    if (name === 'setTaskProximity' && (value <= 0 || !value))
      onChange(name, 10 as any);
    if (name === InGame.leaderboard) setIsEnableLeaderboard(value);
  };

  const isDisableChatSetting = React.useMemo(() => {
    const chatSetting = [
      state.showInGameTeamChat,
      state.showInGameGroupChat,
      state.showInGameDirectChat,
      state.showInGameAllPlayerChat,
      state.showInGameFacilitatorChat
    ];
    return chatSetting.every((c) => c === false);
  }, [
    state.showInGameTeamChat,
    state.showInGameGroupChat,
    state.showInGameDirectChat,
    state.showInGameAllPlayerChat,
    state.showInGameFacilitatorChat
  ]);

  React.useEffect(() => {
    onChange(InGame.chats, !isDisableChatSetting);
  }, [isDisableChatSetting]);

  return (
    <>
      <span className={`d-flex justify-content-end`}>
        <HelpSupport
          title="Game settings guide"
          url="https://forum.catalystglobal.com/t/4655"
        />
      </span>
      <div
        className={'d-flex flex-column gap-1 align-items-start mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Menu + Functions</h2>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Change Player Name</span>
        <RadioButton
          name={InGame.changePlayerName}
          value={true}
          label={'Enable'}
          checked={state.enableInGameChangePlayerName}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.changePlayerName}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          checked={!state.enableInGameChangePlayerName}
          onChange={handleInGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables players to change their name.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Info</span>
        <RadioButton
          name={InGame.information}
          value={true}
          label={'Show'}
          checked={state.showInGameInfo}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.information}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameInfo}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of info tab in the menu.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Teams</span>
        <RadioButton
          name={InGame.teams}
          value={true}
          label={'Show'}
          checked={state.showInGameTeams}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.teams}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameTeams}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of teams tab in menu. Teams tab shows the teams in the game
          <br />
          and each team's members.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Create Teams</span>
        <RadioButton
          name={InGame.createTeams}
          value={true}
          label={'Show'}
          checked={state.showInGameCreateTeams}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.createTeams}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameCreateTeams}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Displayed on the team tab. Enables players to create teams.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Join Team/Leave Team</span>
        <RadioButton
          name={InGame.joinLeaveTeams}
          value={true}
          label={'Show'}
          checked={state.showInGameJoinLeaveTeams}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.joinLeaveTeams}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameJoinLeaveTeams}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Displayed on the team tab. Enables players to join or leave a team.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Inventory</span>
        <RadioButton
          name={InGame.inventory}
          value={true}
          label={'Show'}
          checked={state.showInGameInventory}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.inventory}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameInventory}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of inventory tab in the menu. Displays all available
          inventory, <br />
          player inventory and team inventory.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Titles</span>
        <RadioButton
          name={InGame.roles}
          value={true}
          label={'Show'}
          checked={state.showInGameRoles}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.roles}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameRoles}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of titles tab in the menu. Displays all available titles,
          <br />
          player titles and team titles.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Scoreboard</span>
        <RadioButton
          name={InGame.scoreboard}
          value={true}
          label={'Show'}
          checked={state.showInGameScoreboard}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.scoreboard}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameScoreboard}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of scoreboard in the menu. Displays player/team scores and
          leaderboard.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Player/Team score</span>
        <RadioButton
          name={InGame.playerTeamScore}
          value={true}
          label={'Show'}
          checked={state.showInGamePlayerTeamScore}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.playerTeamScore}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGamePlayerTeamScore}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of the players score on scoreboard. In a team game,
          <br />
          displays the team's score and each team member's score.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Leaderboard</span>
        <RadioButton
          name={InGame.leaderboard}
          value={true}
          label={'Show'}
          checked={state.showInGameLeaderboard}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.leaderboard}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameLeaderboard}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of leaderboard with ranking, names and scores of all
          players/teams <br /> on the scoreboard tab.
        </span>
      </div>

      {/* <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Team chat</span>
        <RadioButton
          name={InGame.teamChat}
          value={true}
          label={'Show'}
          checked={
            state.showInGameTeamChat === true
          }
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.teamChat}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={
            state.showInGameTeamChat === false
          }
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Ability of teams to message other teams.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>All players chat</span>
        <RadioButton
          name={InGame.allPlayerChat}
          value={true}
          label={'Show'}
          checked={
            state.showInGameAllPlayerChat === true
          }
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.allPlayerChat}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={
            state.showInGameAllPlayerChat ===
              false
          }
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Ability of players/teams to message all players in the game.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Direct chat</span>
        <RadioButton
          name={InGame.directChat}
          value={true}
          label={'Show'}
          checked={
            state.showInGameDirectChat === true
          }
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.directChat}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={
            !state.showInGameDirectChat
          }
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Ability of players to message direct to other players in the game.
        </span>
      </div> */}

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Chat</span>
        <RadioButton
          name={InGame.chats}
          value={true}
          label={'Show'}
          checked={state.showInGameChat}
          disabled={isDisableChatSetting}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.chats}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameChat}
          disabled={isDisableChatSetting}
          onChange={handleInGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of chat in the menu.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Facilitator Chat</span>
        <RadioButton
          name={InGame.facilitatorChat}
          value={true}
          label={'Show'}
          checked={state.showInGameFacilitatorChat}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.facilitatorChat}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameFacilitatorChat}
          onChange={handleInGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to message the game controller.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>All Player Chat</span>
        <RadioButton
          name={InGame.allPlayerChat}
          value={true}
          label={'Show'}
          checked={state.showInGameAllPlayerChat}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.allPlayerChat}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameAllPlayerChat}
          onChange={handleInGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to message all players in the game.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Direct Chat</span>
        <RadioButton
          name={InGame.directChat}
          value={true}
          label={'Show'}
          checked={state.showInGameDirectChat}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.directChat}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameDirectChat}
          onChange={handleInGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to message another player.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Team Chat</span>
        <RadioButton
          name={InGame.teamChat}
          value={true}
          label={'Show'}
          checked={state.showInGameTeamChat}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.teamChat}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameTeamChat}
          onChange={handleInGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to message all players in a team.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Group Chat</span>
        <RadioButton
          name={InGame.groupChat}
          value={true}
          label={'Show'}
          checked={state.showInGameGroupChat}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.groupChat}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameGroupChat}
          onChange={handleInGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to message all players in a group.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Trade</span>
        <RadioButton
          name={InGame.trade}
          value={true}
          label={'Show'}
          checked={state.showInGameTrade}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.trade}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameTrade}
          onChange={handleInGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to trade with another player.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>On-Demand Task</span>
        <RadioButton
          name={InGame.onDemandTask}
          value={true}
          label={'Show'}
          checked={state.showInGameOnDemandTask}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.onDemandTask}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showInGameOnDemandTask}
          onChange={handleInGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of on-demand task tab in the menu. The on-demand tab lists
          tasks that
          <br /> players can complete at any time during the game.
        </span>
      </div>

      <div
        className={'d-flex flex-column gap-1 align-items-start mt-4 mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Tasks</h2>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Tasks require proximity</span>
        <RadioButton
          name={InGame.tasksRequireProximity}
          value={true}
          label={'On'}
          checked={state.tasksRequireProximity}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.tasksRequireProximity}
          value={false}
          label={'Off'}
          className={'ml-4'}
          checked={!state.tasksRequireProximity}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Player device must be within the set proximity before a task becomes
          available.
        </span>
      </div>

      <div>
        <div className={'d-flex gap-1 align-items-center mt-2 w-100'}>
          <span className={'mr-10 w-30'}>Set task proximity</span>
          <NumericTextBox
            name={InGame.setTaskProximity}
            className={'mr-1 w-10'}
            value={state?.setTaskProximity ?? 0}
            onChange={handleInGameSettingChange}
            min={0}
            disabled={!state.tasksRequireProximity}
          />
          <span>Meters</span>
        </div>

        <div className={'d-flex gap-1 align-items-center mb-2'}>
          <span className={'mb-2 fs-6 text-black-50'}>
            <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
            Set the proximity required before a task becomes available.
          </span>
        </div>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Pre-message text</span>
        <RadioButton
          name={InGame.premessage}
          value={true}
          label={'On'}
          checked={state.enablePremessageOutsideRange}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.premessage}
          value={false}
          label={'Off'}
          className={'ml-4'}
          checked={!state.enablePremessageOutsideRange}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enable pre-message text when not in range.
        </span>
      </div>

      <div
        className={'d-flex flex-column gap-1 align-items-start mt-4 mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Map Display</h2>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Other players/teams</span>
        <RadioButton
          name={InGame.otherPlayerOrTeam}
          value={true}
          label={'Show'}
          checked={state.showOtherPlayerOrTeamOnMap}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.otherPlayerOrTeam}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showOtherPlayerOrTeamOnMap}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of other players/teams on the map.{' '}
          <i>Note: A player's own icon is always visible.</i>
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Player score</span>
        <RadioButton
          name={InGame.showPlayerScoreOnMap}
          value={true}
          label={'Show'}
          checked={state.showPlayerScoreOnMap}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.showPlayerScoreOnMap}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showPlayerScoreOnMap}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          The player's score is displayed, beside the player icon.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Show detail on click</span>
        <RadioButton
          name={InGame.showPlayerDetailByClickOnMap}
          value={true}
          label={'On'}
          checked={state.showPlayerDetailByClickOnMap}
          onChange={handleInGameSettingChange}
        />

        <RadioButton
          name={InGame.showPlayerDetailByClickOnMap}
          value={false}
          label={'Off'}
          className={'ml-4'}
          checked={!state.showPlayerDetailByClickOnMap}
          onChange={handleInGameSettingChange}
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          When true, player icon is always on display. The icon is clicked to
          show, player name, <br />
          score, and team name. When false, player detail is always shown beside
          the player icon.
        </span>
      </div>
    </>
  );
};

export default InGameSetting;
